import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { AGENT_RATE_NAME, CUSTOMER_RATE_NAME } from "../../utils/authUtils";
import SwitchUserModal from "../modals/SwitchUserModal";
import { getTitleByRoute } from "../../utils/routeTitles";
import useOutsideClick from "../../hooks/useOutsideClick";
import { AppDispatch, persistor, RootState } from "../../redux/store";
import { logoutUser } from "../../redux/reducer/auth/authSlice";
import { resetPagination } from "../../redux/reducer/pagination/paginationSlice";
import { clearAllFilters } from "../../redux/reducer/filterSlice/filterSlice";
import { setTitle } from "../../redux/reducer/navbar/navbarSlice";
import {
  firstLetterCapital,
  capitalizeEachWord,
  useOutsideClickCustom,
} from "../../utils/utils";
import { getApi, deletePayloadApi } from "../../apis/api";
import {
  GET_PARENT_CUSTOMER_DATA,
  GET_CUSTOMER_LIST,
} from "../../apis/end-points";
import { GENERAL_ERROR_MESSAGE } from "../../utils/message";
import {
  setActiveTabInnerChildTab,
  setPopFromPreviousTab,
  setActiveTab,
} from "../../redux/reducer/activeTab/activeTabSlice";
import useFormChangeHandler from "../../hooks/useFormChangeHandler";

import {
  EditProfileIcon,
  LogoutProfileIcon,
  SwtichIcon,
  BackTitleIcon,
  MenuHeaderIcon,
  BackButtonIcon,
  AddIcon,
  Dollar,
  SearchIcon,
} from "../../assets";
import ConfirmModal from "../../components/modals/ConfirmModal";
import { setFormChanged } from "../../redux/reducer/prompt/promptSlice";
import { SearchInput } from "../shared";
import _ from "lodash";
import {
  MASTER_SEARCH_PLACEHOLDER,
  MASTER_SEARCH_TOOL_TIP_TEXT,
} from "../../utils/constants";
import { SALES_REP_ROLE } from "../../utils/salesRepAccess";
import SearchButtonModal from "../modals/SearchButtonModal";
import {
  VERIFY_SESSION_TOKEN_API,
  DELETE_SESSION_TOKEN_API,
} from "../../apis/end-points";
import { SESSION_ID_KEY } from "../../utils/authUtils";
import { lookupTicket } from "../../pages/Order-management/CustomerOrderHistory/tabs/Conversations/services/conversationService";
import {
  TICKET_NUMBER,
  ticketPath,
} from "../../pages/Order-management/CustomerOrderHistory/tabs/Conversations/ticketLink";

export interface AddedByTenantsTypes {
  customerName: string;
  customerId: string;
  customerNumber: string;
  id: string;
}
interface GetCustomerApiResponse {
  addedByTenants: AddedByTenantsTypes[];
  currentCustomer: AddedByTenantsTypes;
  customerUserData: any[];
}
interface RouteWithBackButton {
  path: string | RegExp;
  activeTabs: string[];
  shouldNavigateBack: boolean;
  shouldNavigateToPath?: string;
}

// Array of routes where back button should be shown
const routesWithBackButton: RouteWithBackButton[] = [
  {
    path: /^\/agent-list\/[^/]+$/,
    // activeTabs: ["Weight Zone", "Agent Rates"],
    activeTabs: [],
    shouldNavigateBack: false,
    shouldNavigateToPath: "/agent-list",
  },
  {
    path: /^\/master-agent-list\/[^/]+$/,
    // activeTabs: ["Weight Zone", "Agent Rates"],
    activeTabs: [],
    shouldNavigateBack: false,
    shouldNavigateToPath: "/master-agent-list",
  },
  {
    path: /^\/customer\/rate(\/[^/]+)?$/,
    activeTabs: [],
    shouldNavigateBack: true,
    shouldNavigateToPath: "/customers",
  },
  {
    path: "/email-notification-permissions",
    activeTabs: [],
    shouldNavigateBack: true,
  },
  {
    path: /^\/all-orders\/([^/]+)?$/,
    activeTabs: [],
    shouldNavigateBack: true,
  },
  {
    path: /^\/active-order\/([^/]+)?$/,
    activeTabs: [],
    shouldNavigateBack: true,
  },
  {
    path: /^\/delivered-order\/([^/]+)?$/,
    activeTabs: [],
    shouldNavigateBack: true,
  },
  {
    path: /^\/finalized-order\/([^/]+)?$/,
    activeTabs: [],
    shouldNavigateBack: true,
  },
  {
    path: /^\/cancelled-order\/([^/]+)?$/,
    activeTabs: [],
    shouldNavigateBack: true,
  },
  {
    path: /^\/invoiced-order\/([^/]+)?$/,
    activeTabs: [],
    shouldNavigateBack: true,
  },
  {
    path: /^\/agent-list\/edit\/([^/]+)?$/,
    activeTabs: [],
    shouldNavigateBack: true,
  },
  {
    path: /^\/agent-api\/add/,
    activeTabs: [],
    shouldNavigateBack: true,
    shouldNavigateToPath: "/agent-api",
  },
  {
    path: /^\/agent-api\/edit\/([^/]+)?$/,
    activeTabs: [],
    shouldNavigateBack: true,
    shouldNavigateToPath: "/agent-api",
  },
  {
    path: /^\/invoice-list\/edit\/([^/]+)?$/,
    activeTabs: [],
    shouldNavigateBack: true,
    shouldNavigateToPath: "/invoice-list",
  },
  // User Management
  {
    path: "/users/add",
    activeTabs: [],
    shouldNavigateBack: true,
    shouldNavigateToPath: "/users",
  },
  {
    path: /^\/users\/edit\/[^/]+$/,
    activeTabs: [],
    shouldNavigateBack: true,
    shouldNavigateToPath: "/users",
  },

  // Customer Management
  {
    path: "/customers/add",
    activeTabs: [],
    shouldNavigateBack: true,
    shouldNavigateToPath: "/customers",
  },
  {
    path: /^\/customers\/edit\/[^/]+$/,
    activeTabs: [],
    shouldNavigateBack: true,
    shouldNavigateToPath: "/customers",
  },

  {
    path: "/customer-users/add",
    activeTabs: [],
    shouldNavigateBack: true,
    shouldNavigateToPath: "/customer-users",
  },

  {
    path: /^\/customer-users\/edit\/[^/]+$/,
    activeTabs: [],
    shouldNavigateBack: true,
    shouldNavigateToPath: "/customer-users",
  },

  {
    path: "/customer-items/add",
    activeTabs: [],
    shouldNavigateBack: true,
    shouldNavigateToPath: "/customer-items",
  },

  {
    path: /^\/customer-items\/edit\/[^/]+$/,
    activeTabs: [],
    shouldNavigateBack: true,
    shouldNavigateToPath: "/customer-items",
  },

  // Agent Management
  {
    path: "/agent-request/add",
    activeTabs: [],
    shouldNavigateBack: true,
    shouldNavigateToPath: "/agent-request",
  },
  {
    path: /^\/agent-request\/edit\/[^/]+$/,
    activeTabs: [],
    shouldNavigateBack: true,
    shouldNavigateToPath: "/agent-request",
  },
  {
    path: "/agent-list/add",
    activeTabs: [],
    shouldNavigateBack: true,
    shouldNavigateToPath: "/agent-list",
  },
  {
    path: "/agent-list/additional-charges/add",
    activeTabs: [],
    shouldNavigateBack: true,
    shouldNavigateToPath: "/agent-list/:id",
  },
  {
    path: "/agent-list/additional-charges/edit",
    activeTabs: [],
    shouldNavigateBack: true,
    shouldNavigateToPath: "/agent-list/:id",
  },
  // {
  //   path: /^\/agent-list\/edit\/[^/]+$/,
  //   activeTabs: [],
  //   shouldNavigateBack: true,
  //   shouldNavigateToPath: "/agent-list/:id",
  // },

  // Email Template
  {
    path: "/system-configuration/add-email-template",
    activeTabs: [],
    shouldNavigateBack: true,
    shouldNavigateToPath: "/system-configuration/email-template",
  },
  {
    path: /^\/system-configuration\/edit-email-template\/[^/]+$/,
    activeTabs: [],
    shouldNavigateBack: true,
    shouldNavigateToPath: "/system-configuration/email-template",
  },

  // Additional Charges
  {
    path: "/customers/additional-charges/add",
    activeTabs: [],
    shouldNavigateBack: true,
    shouldNavigateToPath: "/customer/rate/:id",
  },
  {
    path: "/customers/additional-charges/edit",
    activeTabs: [],
    shouldNavigateBack: true,
    shouldNavigateToPath: "/customer/rate/:id",
  },
  // {
  //   path: /^\/customers\/additional-charges\/edit\/[^/]+$/,
  //   activeTabs: [],
  //   shouldNavigateBack: true,
  //   shouldNavigateToPath: "/customer/rate/:id",
  // },

  {
    path: "/invoice-list/add",
    activeTabs: [],
    shouldNavigateBack: true,
    shouldNavigateToPath: "/invoice-list",
  },
  {
    path: /^\/invoice-list\/edit\/[^/]+$/,
    activeTabs: [],
    shouldNavigateBack: true,
    shouldNavigateToPath: "/invoice-list",
  },

  {
    path: "/settings-additional-charges/add",
    activeTabs: [],
    shouldNavigateBack: true,
    shouldNavigateToPath: "/settings-additional-charges",
  },
  {
    path: /^\/settings-additional-charges\/edit\/[^/]+$/,
    activeTabs: [],
    shouldNavigateBack: true,
    shouldNavigateToPath: "/settings-additional-charges",
  },

  {
    path: "/customers/group-invoice-list/add",
    activeTabs: [],
    shouldNavigateBack: true,
    shouldNavigateToPath: "/customer/rate/:id",
  },
  {
    path: "/group-invoice-list/add",
    activeTabs: [],
    shouldNavigateBack: true,
    shouldNavigateToPath: "/group-invoice-list",
  },
  {
    path: "/customers/group-invoice-list/edit",
    activeTabs: [],
    shouldNavigateBack: true,
    shouldNavigateToPath: "/customer/rate/:id",
  },
  {
    path: /^\/group-invoice-list\/edit\/[^/]+$/,
    activeTabs: [],
    shouldNavigateBack: true,
    shouldNavigateToPath: "/group-invoice-list",
  },
  {
    path: /^\/group-invoice-list\/edit\/[^/]+$/,
    activeTabs: [],
    shouldNavigateBack: true,
    shouldNavigateToPath: "/group-invoice-list",
  },
  {
    path: "customer-address-book/add",
    activeTabs: [],
    shouldNavigateBack: true,
    shouldNavigateToPath: "/customer-address-book",
  },
  {
    path: "customer-address-book/edit",
    activeTabs: [],
    shouldNavigateBack: true,
    shouldNavigateToPath: "/customer-address-book",
  },
];
const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const { parentUserId } = useSelector((state: RootState) => state.staticData);
  const location = useLocation();
  const dispatch: AppDispatch = useDispatch();
  const [showProfile, setShowProfile] = useState<boolean>(false);
  const [isMobileView, setIsMobileView] = useState<boolean>(false);
  // State to manage the search keyword
  const [showSearchModal, setShowSearchModal] = useState<boolean>(false);
  const [keyWord, setKeyword] = useState<null>(null);
  const [openSwitchUserModal, setOpenSwitchUserModal] =
    useState<boolean>(false);
  const [agentName, setAgentName] = useState<string>(
    sessionStorage.getItem(AGENT_RATE_NAME) ?? ""
  );
  const [customerName, setCustomerName] = useState<string>(
    sessionStorage.getItem(CUSTOMER_RATE_NAME) ?? ""
  );
  const agentRole = localStorage.getItem("roles") === "agent";

  // const { title, username, userProfile, userRole, userEmail, companyLogo } =
  //   useSelector((state: RootState) => state.navbar);
  const { title, username, userProfile, userRole, userEmail, companyLogo } =
    useSelector((state: RootState) => state.navbar);

  const { activeTabInnerChildTab, previousInnerChildTabStack } = useSelector(
    (state: RootState) => state.activeTab
  );
  const { customerLevel } = useSelector((state: RootState) => state.auth);
  const [switchCustomerData, setSwitchCustomerData] = useState<
    AddedByTenantsTypes[]
  >([]);
  const locationStateData = useSelector(
    (state: RootState) => state.locationState.locationStateData
  );

  const [customerSelectedData, setCustomerSelectedData] =
    useState<AddedByTenantsTypes>({
      customerName: "",
      customerId: "",
      customerNumber: "",
      id: "",
    });
  const { isUserOfOmniMove } = useSelector((state: RootState) => state.auth);

  /**
   * Checks if the current location matches any route that requires a back button
   * and if the activeTabInnerChildTab has required values.
   * @returns {boolean} True if a match is found, otherwise false.
   */
  const shouldShowBackButton = routesWithBackButton.some((route) => {
    const routeMatches =
      typeof route.path === "string"
        ? location.pathname.includes(route.path)
        : route.path.test(location.pathname);

    const activeTabMatches =
      !route.activeTabs.length ||
      (activeTabInnerChildTab &&
        route.activeTabs.includes(activeTabInnerChildTab));

    return routeMatches && activeTabMatches;
  });

  /**
   * Toggles the visibility of the profile dropdown.
   */
  const changeProfileView = () => {
    setShowProfile(!showProfile);
  };

  /**
   * Handles user logout by dispatching the `logoutUser` action and navigating to the sign-in page.
   */
  const handleLogout = async () => {
    // Get sessionId from localStorage before clearing
    const sessionId = localStorage.getItem(SESSION_ID_KEY);

    // Call delete session token API if sessionId exists
    if (sessionId) {
      try {
        await deletePayloadApi(DELETE_SESSION_TOKEN_API, {
          sessionId: sessionId,
        });
      } catch (error) {
        // Log error but don't block logout
        console.error("Failed to delete session token:", error);
      }
    }
    await persistor.purge();
    // Proceed with normal logout
    dispatch(resetPagination());
    dispatch(clearAllFilters());
    dispatch(logoutUser());
    if (process.env.REACT_APP_ENV !== "local") {
      // window.location.href = process.env.REACT_APP_REDIRECT_URL || "/users/sign_in";
      // window.location.href = 'https://stage.dtslastmile.com/users/sign_in';
      window.location.href = "https://uat.dtslastmile.com/users/sign_in";
    } else {
      navigate("/users/sign_in");
    }
  };

  /**
   * Navigates to the user's profile edit page and closes the profile dropdown.
   */
  const handleUserEdit = () => {
    setShowProfile(false);
    navigate("/user/profile");
  };

  /**
   * Determines the behavior for back navigation based on the route's shouldNavigateBack value.
   */

  const handleNavigateBack = () => {
    sessionStorage.removeItem('ltlIntegrationEdit');

    dispatch(setFormChanged(false));
    // --- Pass 1: Look for an exact string match first ---
    let matchedRoute = routesWithBackButton.find(
      (route) =>
        (typeof route?.path === "string" &&
          location?.pathname === route?.path) ||
        (typeof route?.path === "string" &&
          location?.pathname.includes("edit") &&
          location.pathname.split("/").slice(0, -1).join("/") === route?.path)
    );

    // --- Pass 2: If no exact match was found, look for a partial or regex match ---
    if (!matchedRoute) {
      matchedRoute = routesWithBackButton.find((route) => {
        return typeof route.path === "string"
          ? location?.pathname?.includes(route?.path) // Fallback to partial match for strings
          : route.path.test(location.pathname); // Test for regex paths
      });
    }

    // Fallback for new tabs with no browser history: navigate to parent path
    const navigateBackOrFallback = () => {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        // Extract parent path (e.g., "/all-orders/123" -> "/all-orders")
        const parentPath = location.pathname.split("/").slice(0, -1).join("/") || "/";
        navigate(parentPath);
      }
    };

    if (
      matchedRoute?.shouldNavigateBack ||
      activeTabInnerChildTab === "agentRateData"
    ) {
      if (matchedRoute?.shouldNavigateToPath) {
        if (matchedRoute?.shouldNavigateToPath === "/customer/rate/:id") {
          const customerId = location?.state?.data?.customerId;
          navigate(`/customer/rate/${customerId}`, {
            state: {
              from: location.pathname,
              rateType: location.state?.data?.rateType,
            },
          });
          return;
        } else if (matchedRoute?.shouldNavigateToPath === "/agent-list/:id") {
          navigate(`/agent-list/${locationStateData?.id}`, {
            state: { from: location.pathname },
          });
          return;
        }

        navigate(location.state?.from || matchedRoute?.shouldNavigateToPath, {
          state: { from: location.pathname },
        });
      } else {
        navigateBackOrFallback();
      }
    } else {
      if (activeTabInnerChildTab) {
        if ([1, 0, 2, 6]?.includes(previousInnerChildTabStack.length)) {
          // If there is no existing tab, then set to null to remove backIcon
          dispatch(setActiveTabInnerChildTab(null));
        }
      } else {
        if (matchedRoute?.shouldNavigateToPath) {
          navigate(matchedRoute?.shouldNavigateToPath);
        } else {
          navigateBackOrFallback();
        }
      }

      previousInnerChildTabStack.length && dispatch(setPopFromPreviousTab());
    }
  };

  // Hook for handling form change navigation
  const {
    checkInputChange,
    handleNavigation,
    handleConfirmNavigation,
    handleCancelNavigation,
    handleUpdateNavigation,
    showModal,
    isFormChanged,
  } = useFormChangeHandler();

  /**
   * Handles the opening of the Switch User modal.
   */
  const handleSwitchUser = () => {
    setOpenSwitchUserModal(true);
  };

  // Ref to handle clicks outside the dropdown to close it
  const dropdownRef = useOutsideClick<HTMLDivElement>(() => {
    setShowProfile(false);
  });

  /**
   * Fetches the parent customer data from the API.
   * Updates the state with the retrieved data.
   */
  const getParentCustomerData = async () => {
    try {
      const response = await getApi<GetCustomerApiResponse>(
        userEmail
          ? `${GET_CUSTOMER_LIST}?emailId=${encodeURIComponent(userEmail)}`
          : ""
      );
      if (response.success) {
        const data =
          response.data?.addedByTenants ??
          response?.data?.customerUserData ??
          [];

        //Hardcoded need to change

        localStorage.setItem("companyId", parentUserId || "");
        const mappedData: AddedByTenantsTypes[] = data?.map((item) => ({
          id: item?.customerId,
          customerId: item?.customerId,
          customerName: item?.customerName ?? "",
          customerNumber: item?.customerNumber ?? "",
        }));
        setSwitchCustomerData(mappedData);
        const currentCustomer =
          response.data?.currentCustomer ??
          ({} as Partial<AddedByTenantsTypes>);
        setCustomerSelectedData({
          customerName: currentCustomer?.customerName ?? "",
          customerId: currentCustomer?.customerId ?? "",
          customerNumber: currentCustomer?.customerNumber ?? "",
          id: currentCustomer?.customerId ?? "",
        });
      }
    } catch (error) {
      console.error(GENERAL_ERROR_MESSAGE, error);
    }
  };

  const debouncedSearch = useCallback(
    _.debounce((keyword) => {
      if (keyword !== undefined) {
        // Trim only what is searched — the input keeps what the user typed,
        // otherwise a trailing space could never be typed between two words.
        const searchTerm = typeof keyword === "string" ? keyword.trim() : keyword;
        // A ticket number opens the ticket on its order (KAN-1320); otherwise the usual order search
        if (typeof searchTerm === "string" && TICKET_NUMBER.test(searchTerm)) {
          void lookupTicket(searchTerm).then((response) => {
            if (response.success && response.data) navigate(ticketPath(response.data));
            else navigate("/all-orders", { state: { searchTerm } });
          });
          return;
        }
        if (isUserOfOmniMove && !agentRole) {
          navigate("/billed-order-history", { state: { searchTerm } });
        } else {
          navigate("/all-orders", { state: { searchTerm } });
        }
      }
    }, 500),
    []
  );

  const handleKeywordChangeForSearch = useCallback(
    (e: any) => {
      setKeyword(e);
      debouncedSearch(e);
    },
    [debouncedSearch]
  );

  useEffect(() => {
    if (location?.state?.searchTerm) {
      // Clear the state by navigating to the same page without state
      navigate(location.pathname, { replace: true });
    }
    if (
      location?.pathname !== "/all-orders" &&
      location?.pathname !== "/billed-order-history"
    ) {
      setKeyword(null);
    }
  }, [location?.state?.searchTerm, location?.pathname]);

  /**
   * Handles sidebar collapse by toggling the 'sidebar-hidden' class.
   */
  const toggleSidebar = () => {
    document.body.classList.toggle("sidebar-hidden");
  };

  /**
   * Effect to update the page title based on the current route and agent name.
   *
   * @description This effect runs whenever the route changes, updating the navbar title
   * by dispatching the `setTitle` action with the title derived from the current path.
   */
  useEffect(() => {
    const currentTitle =
      location.pathname.split("/")[1] === "customer"
        ? getTitleByRoute(location.pathname, customerName || "")
        : getTitleByRoute(location.pathname, agentName || "");
    dispatch(setTitle(currentTitle));
  }, [location.pathname, dispatch, agentName, customerName]);

  /**
   * Effect to update the agent name from sessionStorage when it changes.
   * sessionStorage is per-tab, so two tabs viewing different agents/customers
   * keep their own title instead of clobbering a shared localStorage value.
   */
  useEffect(() => {
    setAgentName(sessionStorage.getItem(AGENT_RATE_NAME) ?? "");
  }, [sessionStorage.getItem(AGENT_RATE_NAME)]);
  useEffect(() => {
    setCustomerName(sessionStorage.getItem(CUSTOMER_RATE_NAME) ?? "");
  }, [sessionStorage.getItem(CUSTOMER_RATE_NAME)]);

  /**
   * Effect to fetch parent customer data when the component mounts.
   */
  useEffect(() => {
    if (parentUserId) {
      getParentCustomerData();
    }
  }, [parentUserId]);

  useEffect(() => {
    getParentCustomerData();
  }, []);

  const BackButtonClick = () => {
    if (isFormChanged) {
      handleNavigation();
    } else {
      handleNavigateBack();
    }
  };

  const closeSearchModal = () => {
    setShowSearchModal(false);
  };

  // Ref for mobile dropdown
  const mobileDropdownRef = useRef<HTMLDivElement>(null);
  // Custom hook to handle clicks outside the mobile dropdown
  useOutsideClickCustom(mobileDropdownRef, () => {
    setIsMobileView(false);
  });
  // Check if the current route should show action buttons
  const showActionButtons =
    userRole !== "agent" &&
    userRole !== "superadmin" &&
    userRole !== SALES_REP_ROLE &&
    isUserOfOmniMove === false &&
    customerLevel !== "third";

  return (
    <>
      <div className="page__head">
        <h2 className="page__head--title">
          {shouldShowBackButton && (
            <button className="backBtn">
              <BackTitleIcon onClick={BackButtonClick} />
            </button>
          )}
          {title}
        </h2>
        <div className="page__head__actions">
          <div className="btn__group">
            <SearchInput
              type="text"
              name="orderNumber"
              value={keyWord || ""}
              placeholder={MASTER_SEARCH_PLACEHOLDER}
              onChange={(e) => handleKeywordChangeForSearch(e.target.value)}
              title={MASTER_SEARCH_TOOL_TIP_TEXT}
            />
            <button
              className="btn btn__white btn--search"
              onClick={() => setShowSearchModal(!showSearchModal)}
            >
              <SearchIcon />
            </button>
            {/* {userRole !== "agent" &&
              userRole !== "superadmin" &&
              isUserOfOmniMove === false &&
              customerLevel !== "third" && (
                <div className="mobileViewDropdown" ref={mobileDropdownRef}>
                  <button
                    className="btn btn__white"
                    onClick={() => {
                      setIsMobileView(!isMobileView);
                    }}
                  >
                    <AddIcon />
                    Add New
                  </button>
                  <div
                    className={
                      isMobileView
                        ? `mobileViewDropdown__dropdown mobileViewDropdown__dropdown--open`
                        : `mobileViewDropdown__dropdown`
                    }
                  >
                    {userRole !== "agent" &&
                      userRole !== "superadmin" &&
                      isUserOfOmniMove === false &&
                      customerLevel !== "third" && (
                        <button
                          className="btn btn__white"
                          onClick={() => {
                            dispatch(setActiveTab(null));
                            navigate("/customer-order");
                            setIsMobileView(!isMobileView);
                          }}
                        >
                          <AddIcon />
                          Add New Order
                        </button>
                      )}
                    {userRole !== "agent" &&
                      userRole !== "superadmin" &&
                      isUserOfOmniMove === false &&
                      customerLevel !== "third" && (
                        <button
                          className="btn btn__white "
                          onClick={() => {
                            dispatch(setActiveTab(null));
                            navigate("/quick-quote");
                            setIsMobileView(!isMobileView);
                          }}
                        >
                          <Dollar />
                          Quick Quote
                        </button>
                      )}
                  </div>
                </div>
              )} */}
            {showActionButtons && (
              <div className="mobileViewDropdown" ref={mobileDropdownRef}>
                <button
                  className="btn btn__white"
                  onClick={() => {
                    setIsMobileView(!isMobileView);
                  }}
                >
                  <AddIcon />
                  Add New
                </button>

                <div
                  className={
                    isMobileView
                      ? "mobileViewDropdown__dropdown mobileViewDropdown__dropdown--open"
                      : "mobileViewDropdown__dropdown"
                  }
                >
                  <button
                    className="btn btn__white"
                    onClick={() => {
                      dispatch(setActiveTab(null));
                      navigate("/customer-order");
                      setIsMobileView(false); // closes dropdown after click
                    }}
                  >
                    <AddIcon />
                    Add New Order
                  </button>

                  <button
                    className="btn btn__white"
                    onClick={() => {
                      dispatch(setActiveTab(null));
                      navigate("/quick-quote");
                      setIsMobileView(false); // closes dropdown after click
                    }}
                  >
                    <Dollar />
                    Quick Quote
                  </button>
                </div>
              </div>
            )}
            {userRole !== "agent" &&
              userRole !== "superadmin" &&
              userRole !== SALES_REP_ROLE &&
              isUserOfOmniMove === false &&
              customerLevel !== "third" && (
                <button
                  className="btn btn__white btn--hideMobile"
                  onClick={() => {
                    dispatch(setActiveTab(null));
                    navigate("/customer-order");
                  }}
                >
                  <AddIcon />
                  Add New Order
                </button>
              )}
            {userRole !== "agent" &&
              userRole !== "superadmin" &&
              userRole !== SALES_REP_ROLE &&
              isUserOfOmniMove === false &&
              customerLevel !== "third" && (
                <button
                  className="btn btn__white btn--hideMobile"
                  onClick={() => {
                    dispatch(setActiveTab(null));
                    navigate("/quick-quote");
                  }}
                >
                  <Dollar />
                  Quick Quote
                </button>
              )}
          </div>
          <div className="userProfile">
            <button className="userProfile__link" onClick={changeProfileView}>
              <span className="picIcon">
                {/* {customerLevel === "first" &&
                (userRole === "admin" || userRole === "customersuperadmin") &&
                companyLogo ? (
                  <img className="picIcon__img" src={companyLogo} alt="" />
                ) : ( */}
                {userProfile}
                {/* )} */}
              </span>
              {capitalizeEachWord(username ?? "")}
            </button>
            <div
              ref={dropdownRef}
              className={`userProfile__dropdown ${
                showProfile ? "userProfile__dropdown--open" : ""
              }`}
            >
              <div className="userdetials">
                <div className="userdetials__info">
                  <div className="pic">
                    {/* {customerLevel === "first" &&
                    (userRole === "admin" || userRole === "customersuperadmin") &&
                    companyLogo ? (
                      <img className="pic__img" src={companyLogo} alt="" />
                    ) : ( */}
                    {userProfile}
                    {/* )} */}
                  </div>
                  <h3 className="name">{capitalizeEachWord(username ?? "")}</h3>
                  <h4 className="role">
                    {userRole !== "customersuperadmin" &&
                      (userRole === SALES_REP_ROLE
                        ? "Sales Rep"
                        : firstLetterCapital(
                            customerLevel === "third" && userRole === "customer"
                              ? "Super Admin"
                              : userRole ?? ""
                          ))}
                    {userRole === "customersuperadmin" && "Super Admin"}
                  </h4>
                  <p className="para">{userEmail}</p>
                </div>
                <div className="btn__group">
                  <div className="btn__group__item">
                    <button
                      type="button"
                      className="btn btn__primary"
                      onClick={handleUserEdit}
                    >
                      <EditProfileIcon />
                      Edit Profile
                    </button>
                  </div>
                  {!switchCustomerData?.length && (
                    <div className="btn__group__item">
                      <button
                        type="button"
                        onClick={handleLogout}
                        className="btn btn__danger"
                      >
                        <LogoutProfileIcon />
                        Logout
                      </button>
                    </div>
                  )}
                  {switchCustomerData &&
                    ((userRole === "agent" && switchCustomerData.length > 0) ||
                      (userRole != "agent" &&
                        switchCustomerData.length > 1)) && (
                      <div className="btn__group__item">
                        <button
                          type="button"
                          className="btn btn__primary"
                          onClick={handleSwitchUser}
                        >
                          <SwtichIcon />
                          Switch Account
                        </button>
                      </div>
                    )}

                  {switchCustomerData.length > 0 && (
                    <div className="btn__group__item">
                      <button
                        type="button"
                        onClick={handleLogout}
                        className="btn btn__danger"
                      >
                        <LogoutProfileIcon />
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <button
            className="btnCollapse"
            id="sidebarCollapse"
            onClick={toggleSidebar}
          >
            <MenuHeaderIcon />
            <svg
              className="btnCollapse__closeIcon"
              width="23"
              height="23"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13 1L1 13M1 1L13 13"
                stroke="#ffffff"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
      {openSwitchUserModal && (
        <SwitchUserModal
          handleClose={() => setOpenSwitchUserModal(false)}
          // switchCustomerData={switchCustomerData}
          // setSwitchCustomerData={setSwitchCustomerData}
          customerSelectedData={customerSelectedData}
          setCustomerSelectedData={setCustomerSelectedData}
        />
      )}
      {showModal && (
        <ConfirmModal
          confirmInfo={{
            heading: "Unsaved Changes!",
            para: "You have not saved your changes, do you want to continue",
          }}
          onConfirm={handleConfirmNavigation}
          setConfirmModal={handleCancelNavigation}
          firstMessageShow={true}
        />
      )}
      {showSearchModal && (
        <SearchButtonModal closeSearchModal={closeSearchModal} />
      )}
    </>
  );
};

export default Navbar;
